import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Thead,
  Th,
  Tr,
  Table,
  Td,
  Tbody,
  Flex,
  Badge,
  TableContainer,
  StatGroup,
  StatArrow,
  StatHelpText,
  StatNumber,
  Stat,
  StatLabel,
  Spinner,
  Button,
  Icon,
  Link as ChakraLink,
  useDisclosure,
  Tooltip,
  Text,
  Switch,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { SortAscending, SortDescending, Equals, ArrowsClockwise } from '@phosphor-icons/react';
import { RouteComponentProps } from '@reach/router';
import { useContext, useState, useEffect } from 'react';
import moment from 'moment';

import ConsigneeScanModal from './ConsigneeScanModal';
import DriverDropOffScanModal from './DriverDropOffScanModal';
import AgedParcelScanModal from './AgedParcelScanModal';

import { formatUTCToLocal } from '../../../utils';
import { parcelStatusText } from '../../parcelStatus';
import { statusColorScheme } from '../../Users/Tabs/UPSParcels';
import {
  GET_PARCELS,
  GET_PARCEL_STATISTICS,
  InternalAllUPSParcelsQuery,
  InternalUPSParcelStatisticsQuery,
  DateFilters,
  ParcelSortField,
} from './query';
import { Pagination } from '../../../components/Pagination';
import Layout from '../../../components/Layout';
import { FilterSelectPill } from '../../../components/FilterPill/Select';
import { FilterTextInputPill } from '../../../components/FilterPill/TextInput';
import { FilterDateRangePill } from '../../../components/FilterPill/DateRange';
import UserContext from '../../../contexts/UserContext';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { SET_PARCEL_HIDDEN_IN_APP } from '../../Users/query';

interface AccessPointsParcelsProps extends RouteComponentProps {}

// Helper functions for URL management
const getUrlParams = () => new URLSearchParams(window.location.search);

const getArrayFromUrl = (params: URLSearchParams, key: string): string[] => {
  const value = params.get(key);
  return value ? value.split(',').filter(Boolean) : [];
};

const getDateRangeFromUrl = (params: URLSearchParams, prefix: string) => {
  return {
    startDate: params.get(`${prefix}From`) || '',
    endDate: params.get(`${prefix}To`) || '',
  };
};

// Polling interval in milliseconds (10 seconds)
const POLLING_INTERVAL = 10000;

export const AccessPointsParcels: React.FC<AccessPointsParcelsProps> = () => {
  const limit = 50;
  const { user } = useContext(UserContext);
  const toast = useCustomToast();
  const [setParcelHiddenInApp] = useMutation(SET_PARCEL_HIDDEN_IN_APP);
  const { isOpen: isConsigneeScanOpen, onOpen: onConsigneeScanOpen, onClose: onConsigneeScanClose } = useDisclosure();
  const { isOpen: isDriverDropOffOpen, onOpen: onDriverDropOffOpen, onClose: onDriverDropOffClose } = useDisclosure();
  const { isOpen: isAgedParcelScanOpen, onOpen: onAgedParcelScanOpen, onClose: onAgedParcelScanClose } = useDisclosure();
  const [selectedParcel, setSelectedParcel] = useState<{
    trackingNumber: string;
    accessPointID: string;
    epraIndicator?: boolean;
  } | null>(null);

  // Auto-refresh toggle state
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Initialize state from URL
  const params = getUrlParams();
  const [offset, setOffset] = useState(parseInt(params.get('offset') || '0', 10));
  const [statuses, setStatuses] = useState(getArrayFromUrl(params, 'statuses').length > 0 ? getArrayFromUrl(params, 'statuses') : []);
  const [parcelDetails, setParcelDetails] = useState(getArrayFromUrl(params, 'parcelDetails'));
  const [accessPointIDs, setAccessPointIDs] = useState(getArrayFromUrl(params, 'accessPointIDs'));
  const [trackingNumberSearch, setTrackingNumberSearch] = useState(params.get('trackingNumber') || '');
  const [sortBy, setSortBy] = useState<ParcelSortField>((params.get('sortBy') as ParcelSortField) || 'createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>((params.get('sortOrder') as 'asc' | 'desc') || 'desc');

  // Date filter states
  const [createdAtRange, setCreatedAtRange] = useState(getDateRangeFromUrl(params, 'createdAt'));
  const [updatedAtRange, setUpdatedAtRange] = useState(getDateRangeFromUrl(params, 'updatedAt'));
  const [shipperDropOffAtRange, setShipperDropOffAtRange] = useState(getDateRangeFromUrl(params, 'shipperDropOffAt'));
  const [driverDropOffAtRange, setDriverDropOffAtRange] = useState(getDateRangeFromUrl(params, 'driverDropOffAt'));
  const [consigneePickupAtRange, setConsigneePickupAtRange] = useState(getDateRangeFromUrl(params, 'consigneePickupAt'));
  const [parcelAgedAtRange, setParcelAgedAtRange] = useState(getDateRangeFromUrl(params, 'parcelAgedAt'));
  const [dispossessedAtRange, setDispossessedAtRange] = useState(getDateRangeFromUrl(params, 'dispossessedAt'));

  // Boolean filter states
  const [scanEventFilters, setScanEventFilters] = useState<string[]>(getArrayFromUrl(params, 'scanEvents'));

  // Sync state with URL
  useEffect(() => {
    const params = new URLSearchParams();

    // Default statuses
    const defaultStatuses = [];
    const isDefaultStatuses = statuses.length === defaultStatuses.length && statuses.every((s) => defaultStatuses.includes(s));

    // Add filters to URL
    if (offset > 0) params.set('offset', offset.toString());
    if (trackingNumberSearch) params.set('trackingNumber', trackingNumberSearch);
    if (statuses.length > 0 && !isDefaultStatuses) params.set('statuses', statuses.join(','));
    if (parcelDetails.length > 0) params.set('parcelDetails', parcelDetails.join(','));
    if (accessPointIDs.length > 0) params.set('accessPointIDs', accessPointIDs.join(','));
    if (scanEventFilters.length > 0) params.set('scanEvents', scanEventFilters.join(','));

    // Add sort params
    if (sortBy !== 'createdAt') params.set('sortBy', sortBy);
    if (sortOrder !== 'asc') params.set('sortOrder', sortOrder);

    // Add date ranges
    if (createdAtRange.startDate) params.set('createdAtFrom', createdAtRange.startDate);
    if (createdAtRange.endDate) params.set('createdAtTo', createdAtRange.endDate);
    if (updatedAtRange.startDate) params.set('updatedAtFrom', updatedAtRange.startDate);
    if (updatedAtRange.endDate) params.set('updatedAtTo', updatedAtRange.endDate);
    if (shipperDropOffAtRange.startDate) params.set('shipperDropOffAtFrom', shipperDropOffAtRange.startDate);
    if (shipperDropOffAtRange.endDate) params.set('shipperDropOffAtTo', shipperDropOffAtRange.endDate);
    if (driverDropOffAtRange.startDate) params.set('driverDropOffAtFrom', driverDropOffAtRange.startDate);
    if (driverDropOffAtRange.endDate) params.set('driverDropOffAtTo', driverDropOffAtRange.endDate);
    if (consigneePickupAtRange.startDate) params.set('consigneePickupAtFrom', consigneePickupAtRange.startDate);
    if (consigneePickupAtRange.endDate) params.set('consigneePickupAtTo', consigneePickupAtRange.endDate);
    if (parcelAgedAtRange.startDate) params.set('parcelAgedAtFrom', parcelAgedAtRange.startDate);
    if (parcelAgedAtRange.endDate) params.set('parcelAgedAtTo', parcelAgedAtRange.endDate);
    if (dispossessedAtRange.startDate) params.set('dispossessedAtFrom', dispossessedAtRange.startDate);
    if (dispossessedAtRange.endDate) params.set('dispossessedAtTo', dispossessedAtRange.endDate);

    // Update URL without reloading page
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [
    offset,
    trackingNumberSearch,
    statuses,
    parcelDetails,
    accessPointIDs,
    scanEventFilters,
    sortBy,
    sortOrder,
    createdAtRange,
    updatedAtRange,
    shipperDropOffAtRange,
    driverDropOffAtRange,
    consigneePickupAtRange,
    parcelAgedAtRange,
    dispossessedAtRange,
  ]);

  // Build date filters object
  const buildDateFilters = (): DateFilters | undefined => {
    const filters: DateFilters = {};

    if (createdAtRange.startDate) filters.createdAtFrom = createdAtRange.startDate;
    if (createdAtRange.endDate) filters.createdAtTo = createdAtRange.endDate;
    if (updatedAtRange.startDate) filters.updatedAtFrom = updatedAtRange.startDate;
    if (updatedAtRange.endDate) filters.updatedAtTo = updatedAtRange.endDate;
    if (shipperDropOffAtRange.startDate) filters.shipperDropOffAtFrom = shipperDropOffAtRange.startDate;
    if (shipperDropOffAtRange.endDate) filters.shipperDropOffAtTo = shipperDropOffAtRange.endDate;
    if (driverDropOffAtRange.startDate) filters.driverDropOffAtFrom = driverDropOffAtRange.startDate;
    if (driverDropOffAtRange.endDate) filters.driverDropOffAtTo = driverDropOffAtRange.endDate;
    if (consigneePickupAtRange.startDate) filters.consigneePickupAtFrom = consigneePickupAtRange.startDate;
    if (consigneePickupAtRange.endDate) filters.consigneePickupAtTo = consigneePickupAtRange.endDate;
    if (parcelAgedAtRange.startDate) filters.parcelAgedAtFrom = parcelAgedAtRange.startDate;
    if (parcelAgedAtRange.endDate) filters.parcelAgedAtTo = parcelAgedAtRange.endDate;
    if (dispossessedAtRange.startDate) filters.dispossessedAtFrom = dispossessedAtRange.startDate;
    if (dispossessedAtRange.endDate) filters.dispossessedAtTo = dispossessedAtRange.endDate;

    if (scanEventFilters.includes('hasDriverDropOff')) filters.hasDriverDropOff = true;
    if (scanEventFilters.includes('hasConsigneePickup')) filters.hasConsigneePickup = true;
    if (scanEventFilters.includes('hasShipperDropOff')) filters.hasShipperDropOff = true;
    if (scanEventFilters.includes('hasParcelAged')) filters.hasParcelAged = true;

    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  const { data, loading, refetch } = useQuery<InternalAllUPSParcelsQuery>(GET_PARCELS, {
    variables: {
      limit,
      offset,
      sortBy,
      sortOrder,
      statuses,
      accessPointIDs,
      trackingNumberSearch: trackingNumberSearch || undefined,
      dateFilters: buildDateFilters(),
      parcelDetails:
        parcelDetails.length > 0
          ? {
              epraIndicator: parcelDetails.includes('EPRA') ? true : undefined,
              addresseeOnlyIndicator: parcelDetails.includes('ADDRESSEE_ONLY') ? true : undefined,
              adultSignatureIndicator: parcelDetails.includes('ADULT_SIGNATURE') ? true : undefined,
            }
          : null,
    },
    pollInterval: autoRefresh ? POLLING_INTERVAL : 0,
  });

  const { data: statisticsData, loading: statisticsLoading } = useQuery<InternalUPSParcelStatisticsQuery>(GET_PARCEL_STATISTICS);

  const handleLoadMore = (page: number) => {
    setOffset((page - 1) * limit);
  };

  const handleSort = (field: ParcelSortField) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortBy(field);
      setSortOrder('asc');
    }
    setOffset(0); // Reset to first page when sorting changes
  };

  const renderSortIcon = (field: ParcelSortField) => {
    if (sortBy !== field) {
      return <Equals size={16} style={{ display: 'inline', marginLeft: '0.25rem', opacity: 0.4, verticalAlign: 'middle' }} />;
    }
    return sortOrder === 'asc' ? (
      <SortAscending size={16} style={{ display: 'inline', marginLeft: '0.25rem', verticalAlign: 'middle' }} />
    ) : (
      <SortDescending size={16} style={{ display: 'inline', marginLeft: '0.25rem', verticalAlign: 'middle' }} />
    );
  };

  const getCountByStatus = (status: string) => {
    return statisticsData?.internalUPSParcelStatistics.byStatus.find((data) => data.status === status)?.count || 0;
  };

  const thisMonth = statisticsData?.internalUPSParcelStatistics.historicalData.find((data) => data.period === moment().format('YYYY-MM'));
  const lastMonth = statisticsData?.internalUPSParcelStatistics.historicalData.find(
    (data) => data.period === moment().subtract(1, 'month').format('YYYY-MM')
  );
  const lastMonthCount = lastMonth?.count || 0;
  const thisMonthCount = thisMonth?.count || 0;

  return (
    <Layout fullWidth>
      <Box mb="4">
        <StatGroup>
          <Stat>
            <StatLabel>Aktive Access Points</StatLabel>
            {statisticsLoading ? (
              <Spinner />
            ) : (
              <>
                <StatNumber>{statisticsData?.internalUPSParcelStatistics.activeAccessPoints}</StatNumber>
                <StatHelpText>({statisticsData?.internalUPSParcelStatistics.totalAccessPoints} insgesamt)</StatHelpText>
              </>
            )}
          </Stat>
          <Stat>
            <StatLabel>Pakete {moment().format('MMM YYYY')}</StatLabel>
            {statisticsLoading ? (
              <Spinner />
            ) : (
              <>
                <StatNumber>{thisMonthCount}</StatNumber>
                <StatHelpText>
                  <StatArrow type={thisMonthCount > lastMonthCount ? 'increase' : 'decrease'} />
                  {Math.round(lastMonthCount ? ((thisMonthCount - lastMonthCount) / lastMonthCount) * 100 : 0)}%
                </StatHelpText>
              </>
            )}
          </Stat>
          <Stat>
            <StatLabel>Pakete {moment().subtract(1, 'month').format('MMM YYYY')}</StatLabel>
            {statisticsLoading ? (
              <Spinner />
            ) : (
              <>
                <StatNumber>{lastMonthCount}</StatNumber>
              </>
            )}
          </Stat>
          <Stat>
            <StatLabel>Pakete INBOUND</StatLabel>
            {statisticsLoading ? (
              <Spinner />
            ) : (
              <>
                <StatNumber>{getCountByStatus('INBOUND')}</StatNumber>
              </>
            )}
            <StatHelpText>im Inventar</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Pakete DISPOSSESSIONS</StatLabel>
            {statisticsLoading ? (
              <Spinner />
            ) : (
              <>
                <StatNumber>{getCountByStatus('DISPOSSESSIONS')}</StatNumber>
              </>
            )}
            <StatHelpText>Gesamt</StatHelpText>
          </Stat>
        </StatGroup>
      </Box>
      <Box mb="4">
        <StatGroup>
          <Stat>
            <StatLabel>DISPOSSESSIONS {moment().format('MMM YYYY')}</StatLabel>
            {statisticsLoading ? (
              <Spinner />
            ) : (
              <>
                <StatNumber>{statisticsData?.internalUPSParcelStatistics.dispossessionsThisMonth}</StatNumber>
                <StatHelpText>
                  <StatArrow
                    type={
                      statisticsData?.internalUPSParcelStatistics.dispossessionsLastMonth <
                      statisticsData?.internalUPSParcelStatistics.dispossessionsThisMonth
                        ? 'increase'
                        : 'decrease'
                    }
                  />
                  {Math.round(
                    statisticsData?.internalUPSParcelStatistics.dispossessionsThisMonth
                      ? ((statisticsData?.internalUPSParcelStatistics.dispossessionsThisMonth -
                          statisticsData?.internalUPSParcelStatistics.dispossessionsLastMonth) /
                          statisticsData?.internalUPSParcelStatistics.dispossessionsLastMonth) *
                          100
                      : 0
                  )}
                  %
                </StatHelpText>
              </>
            )}
          </Stat>
          <Stat>
            <StatLabel>DISPOSSESSIONS {moment().subtract(1, 'month').format('MMM YYYY')}</StatLabel>
            {statisticsLoading ? (
              <Spinner />
            ) : (
              <>
                <StatNumber>{statisticsData?.internalUPSParcelStatistics.dispossessionsLastMonth}</StatNumber>
              </>
            )}
          </Stat>
          <Stat></Stat>
          <Stat></Stat>
          <Stat></Stat>
        </StatGroup>
      </Box>

      <Flex justifyContent="flex-start" alignItems="center" gap="2" mb="2" flexWrap="wrap">
        <FilterTextInputPill
          label="Trackingnummer"
          placeholder="Trackingnummer eingeben..."
          initialValue={trackingNumberSearch}
          setValue={(value) => {
            setTrackingNumberSearch(value);
            setOffset(0);
          }}
          clear={() => {
            setTrackingNumberSearch('');
            setOffset(0);
          }}
        />
        <FilterTextInputPill
          label="AccessPoints"
          placeholder="AP1, AP2, AP3..."
          initialValue={accessPointIDs.join(', ')}
          setValue={(value) => {
            const ids = value
              .split(',')
              .map((id) => id.trim())
              .filter(Boolean);
            setAccessPointIDs(ids);
            setOffset(0);
          }}
          clear={() => {
            setAccessPointIDs([]);
            setOffset(0);
          }}
        />
        <FilterSelectPill
          label="Status"
          options={statisticsData?.internalUPSParcelStatistics.byStatus.map((data) => ({
            label: `${parcelStatusText(data.status)} (${getCountByStatus(data.status)})`,
            value: data.status,
          }))}
          initialValues={statuses}
          setValues={(values) => {
            setStatuses(values);
            setOffset(0);
          }}
          clear={() => {
            setStatuses([]);
            setOffset(0);
          }}
        />
        <FilterSelectPill
          label="Paketdetails"
          options={['EPRA', 'ADDRESSEE_ONLY', 'ADULT_SIGNATURE'].map((data) => ({
            label: data,
            value: data,
          }))}
          initialValues={parcelDetails}
          setValues={(values) => {
            setParcelDetails(values);
            setOffset(0);
          }}
          clear={() => {
            setParcelDetails([]);
            setOffset(0);
          }}
        />
      </Flex>

      <Flex justifyContent="flex-start" alignItems="center" gap="2" mb="4" flexWrap="wrap">
        <FilterDateRangePill
          label="Erstellt am"
          initialValues={createdAtRange}
          setValues={(values) => {
            setCreatedAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setCreatedAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Aktualisiert am"
          initialValues={updatedAtRange}
          setValues={(values) => {
            setUpdatedAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setUpdatedAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Fahrer Drop-Off"
          initialValues={driverDropOffAtRange}
          setValues={(values) => {
            setDriverDropOffAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setDriverDropOffAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Empfänger Abholung"
          initialValues={consigneePickupAtRange}
          setValues={(values) => {
            setConsigneePickupAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setConsigneePickupAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Versender Drop-Off"
          initialValues={shipperDropOffAtRange}
          setValues={(values) => {
            setShipperDropOffAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setShipperDropOffAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Paket aged"
          initialValues={parcelAgedAtRange}
          setValues={(values) => {
            setParcelAgedAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setParcelAgedAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Dispossessed at"
          initialValues={dispossessedAtRange}
          setValues={(values) => {
            setDispossessedAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setDispossessedAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterSelectPill
          label="Scan Events"
          options={[
            { label: 'Hat Fahrer Drop-Off', value: 'hasDriverDropOff' },
            { label: 'Hat Empfänger Abholung', value: 'hasConsigneePickup' },
            { label: 'Hat Versender Drop-Off', value: 'hasShipperDropOff' },
            { label: 'Hat Paket gealtert', value: 'hasParcelAged' },
          ]}
          initialValues={scanEventFilters}
          setValues={(values) => {
            setScanEventFilters(values);
            setOffset(0);
          }}
          clear={() => {
            setScanEventFilters([]);
            setOffset(0);
          }}
        />
      </Flex>

      {/* Refresh Controls */}
      <Flex justifyContent="flex-end" alignItems="center" gap="4" mb="2">
        <FormControl display="flex" alignItems="center" width="auto">
          <Switch id="auto-refresh" isChecked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} colorScheme="blue" />
          <FormLabel htmlFor="auto-refresh" mb="0" fontSize="sm" ml="2">
            Auto-Refresh (10s)
          </FormLabel>
        </FormControl>
        <Tooltip label="Tabelle aktualisieren" hasArrow>
          <Button size="sm" variant="outline" leftIcon={<ArrowsClockwise size={16} />} onClick={() => refetch()} isLoading={loading}>
            Aktualisieren
          </Button>
        </Tooltip>
      </Flex>

      <Box>
        <TableContainer>
          <Table variant="striped" size="sm">
            <Thead>
              <Tr>
                <Th>Tracking Number</Th>
                <Th>Status & Infos</Th>
                <Th>AccessPoint</Th>
                <Th>Addressat</Th>
                <Th cursor="pointer" userSelect="none" onClick={() => handleSort('createdAt')}>
                  <Flex alignItems="center" gap="2">
                    <span>1. Sync am</span>
                    {renderSortIcon('createdAt')}
                  </Flex>
                </Th>
                <Th cursor="pointer" userSelect="none" onClick={() => handleSort('updatedAt')}>
                  <Flex alignItems="center" gap="2">
                    <span>Last Update</span>
                    {renderSortIcon('updatedAt')}
                  </Flex>
                </Th>
                <Th>Driver Drop-Off</Th>
                <Th>Consignee Pickup</Th>
                <Th>Parcel Aged</Th>
                <Th>Aged Parcel</Th>
                <Th>Shipper Drop-Off</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            {loading ? null : (
              <Tbody>
                {data?.internalAllUPSParcels.parcels.map((parcel) => (
                  <Tr key={parcel.trackingNumber}>
                    <Td>{parcel.trackingNumber}</Td>
                    <Td>
                      <Flex flexWrap="wrap" gap="2">
                        <Tooltip label={parcel.deliveryStatus} hasArrow>
                          <Badge colorScheme={statusColorScheme[parcel.deliveryStatus]}>{parcelStatusText(parcel.deliveryStatus)}</Badge>
                        </Tooltip>
                        {parcel.notInInventorySync && parcel.deliveryStatus !== 'DISPOSSESSIONS' ? (
                          <Badge colorScheme="red">Nicht mehr im Inventar</Badge>
                        ) : null}
                        {parcel.hiddenInApp && <Badge colorScheme="orange">Ausgeblendet</Badge>}
                      </Flex>
                      {parcel.epraIndicator || parcel.addresseeOnlyIndicator || parcel.adultSignatureIndicator ? (
                        <Flex flexWrap="wrap" gap="2" mt="2">
                          {parcel.epraIndicator && <Badge colorScheme="purple">Hat EPRA PIN</Badge>}
                          {parcel.addresseeOnlyIndicator && <Badge colorScheme="blue">Abholung nur durch Addressat</Badge>}
                          {parcel.adultSignatureIndicator && <Badge colorScheme="blue">Abholung durch Erwachsene</Badge>}
                        </Flex>
                      ) : null}
                    </Td>
                    <Td>
                      {parcel.userId ? (
                        <ChakraLink href={`/users/${parcel.userId}`} target="_blank" rel="noopener noreferrer" color="blue.500">
                          {parcel.accessPointID}
                        </ChakraLink>
                      ) : (
                        parcel.accessPointID
                      )}
                    </Td>
                    <Td>{parcel.consigneeName}</Td>
                    <Td>{formatUTCToLocal(parcel.createdAt)}</Td>
                    <Td>{formatUTCToLocal(parcel.updatedAt)}</Td>
                    <Td>
                      {!parcel.driverDropOffAt && !parcel.driverDropOffAckAt ? (
                        '-'
                      ) : (
                        <Box fontSize="xs">
                          <Box>Scan At: {formatUTCToLocal(parcel.driverDropOffAt) || '-'}</Box>
                          <Box>Success: {formatUTCToLocal(parcel.driverDropOffAckAt) || '-'}</Box>
                        </Box>
                      )}
                    </Td>
                    <Td>
                      {!parcel.consigneePickupAt && !parcel.consigneePickupAckAt ? (
                        '-'
                      ) : (
                        <Box fontSize="xs">
                          <Box>Scan At: {formatUTCToLocal(parcel.consigneePickupAt) || '-'}</Box>
                          <Box>Success: {formatUTCToLocal(parcel.consigneePickupAckAt) || '-'}</Box>
                        </Box>
                      )}
                    </Td>
                    <Td>{formatUTCToLocal(parcel.parcelAgedAt) || '-'}</Td>
                    <Td>
                      {!parcel.agedParcelScanAt && !parcel.agedParcelScanAckAt ? (
                        '-'
                      ) : (
                        <Box fontSize="xs">
                          <Box>Scan At: {formatUTCToLocal(parcel.agedParcelScanAt) || '-'}</Box>
                          <Box>Success: {formatUTCToLocal(parcel.agedParcelScanAckAt) || '-'}</Box>
                        </Box>
                      )}
                    </Td>
                    <Td>
                      {!parcel.shipperDropOffAt && !parcel.shipperDropOffAckAt ? (
                        '-'
                      ) : (
                        <Box fontSize="xs">
                          <Box>Scan At: {formatUTCToLocal(parcel.shipperDropOffAt) || '-'}</Box>
                          <Box>Success: {formatUTCToLocal(parcel.shipperDropOffAckAt) || '-'}</Box>
                        </Box>
                      )}
                    </Td>
                    <Td>
                      <Flex gap="1" flexWrap="wrap">
                        {(parcel.deliveryStatus === 'INBOUND' && !parcel.notInInventorySync) ||
                        (parcel.deliveryStatus === 'READY_FOR_CONSIGNEE_PICKUP' &&
                          !parcel.notInInventorySync &&
                          !parcel.driverDropOffAckAt) ? (
                          <Tooltip label="Simuliert den Scan, wenn der Fahrer das Paket abgibt" hasArrow>
                            <Button
                              size="xs"
                              colorScheme="gray"
                              variant="outline"
                              onClick={() => {
                                setSelectedParcel({
                                  trackingNumber: parcel.trackingNumber,
                                  accessPointID: parcel.accessPointID,
                                  epraIndicator: parcel.epraIndicator,
                                });
                                onDriverDropOffOpen();
                              }}
                            >
                              Driver Drop-Off
                            </Button>
                          </Tooltip>
                        ) : null}
                        {(parcel.deliveryStatus === 'READY_FOR_CONSIGNEE_PICKUP' || parcel.deliveryStatus === 'READY_FOR_UPS_PICKUP') &&
                        !parcel.notInInventorySync &&
                        !parcel.consigneePickupAckAt ? (
                          <Tooltip
                            label={parcel.epraIndicator ? 'EPRA PIN erforderlich' : 'Simuliert die Abholung durch den Empfänger'}
                            hasArrow
                          >
                            <Button
                              size="xs"
                              colorScheme={parcel.epraIndicator ? 'purple' : 'blue'}
                              variant="outline"
                              onClick={() => {
                                setSelectedParcel({
                                  trackingNumber: parcel.trackingNumber,
                                  accessPointID: parcel.accessPointID,
                                  epraIndicator: parcel.epraIndicator,
                                });
                                onConsigneeScanOpen();
                              }}
                            >
                              Consignee {parcel.epraIndicator ? '(EPRA)' : ''}
                            </Button>
                          </Tooltip>
                        ) : null}
                        {parcel.deliveryStatus === 'READY_FOR_UPS_PICKUP' && !parcel.notInInventorySync && !parcel.agedParcelScanAckAt ? (
                          <Tooltip label="Simuliert den Scan für ein gealtertes Paket" hasArrow>
                            <Button
                              size="xs"
                              colorScheme="orange"
                              variant="outline"
                              onClick={() => {
                                setSelectedParcel({
                                  trackingNumber: parcel.trackingNumber,
                                  accessPointID: parcel.accessPointID,
                                  epraIndicator: parcel.epraIndicator,
                                });
                                onAgedParcelScanOpen();
                              }}
                            >
                              Aged Parcel
                            </Button>
                          </Tooltip>
                        ) : null}
                        <Tooltip label={parcel.hiddenInApp ? 'Paket in der App einblenden' : 'Paket in der App ausblenden'} hasArrow>
                          <Button
                            size="xs"
                            colorScheme={parcel.hiddenInApp ? 'green' : 'orange'}
                            variant="outline"
                            onClick={async () => {
                              try {
                                await setParcelHiddenInApp({
                                  variables: {
                                    accessPointID: parcel.accessPointID,
                                    trackingNumber: parcel.trackingNumber,
                                    hidden: !parcel.hiddenInApp,
                                    internalUserId: user.id,
                                  },
                                });
                                toast({
                                  title: 'Erfolg!',
                                  description: parcel.hiddenInApp ? 'Paket wurde eingeblendet.' : 'Paket wurde ausgeblendet.',
                                  status: 'success',
                                });
                                refetch();
                              } catch (error) {
                                toast({
                                  description: 'Ein Fehler ist aufgetreten!',
                                  status: 'error',
                                });
                              }
                            }}
                          >
                            {parcel.hiddenInApp ? 'Einblenden' : 'Ausblenden'}
                          </Button>
                        </Tooltip>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            )}
          </Table>
        </TableContainer>
        {loading ? (
          <Flex width="100%" height="100%" justifyContent="center" alignItems="center" p="12">
            <Spinner />
          </Flex>
        ) : null}
        <Box>
          <Pagination
            totalAmount={data?.internalAllUPSParcels.totalCount}
            amountPerPage={limit}
            currentPage={offset / limit + 1}
            handleOnClick={handleLoadMore}
          />
        </Box>
      </Box>

      {selectedParcel && (
        <ConsigneeScanModal
          isOpen={isConsigneeScanOpen}
          onClose={() => {
            onConsigneeScanClose();
            setSelectedParcel(null);
          }}
          refetch={refetch}
          trackingNumber={selectedParcel.trackingNumber}
          accessPointID={selectedParcel.accessPointID}
          epraIndicator={selectedParcel.epraIndicator}
        />
      )}

      {selectedParcel && (
        <DriverDropOffScanModal
          isOpen={isDriverDropOffOpen}
          onClose={() => {
            onDriverDropOffClose();
            setSelectedParcel(null);
          }}
          refetch={refetch}
          trackingNumber={selectedParcel.trackingNumber}
          accessPointID={selectedParcel.accessPointID}
        />
      )}

      {selectedParcel && (
        <AgedParcelScanModal
          isOpen={isAgedParcelScanOpen}
          onClose={() => {
            onAgedParcelScanClose();
            setSelectedParcel(null);
          }}
          refetch={refetch}
          trackingNumber={selectedParcel.trackingNumber}
          accessPointID={selectedParcel.accessPointID}
        />
      )}
    </Layout>
  );
};
